import { DateTime } from 'luxon';
import { makeAutoObservable } from 'mobx';
import {
  CalculationApi,
  RechargeValueApiResponse,
  TransactionApiResponse,
  TransactionMatchApiResponse,
  TransactionSource,
} from '../../api/CalculationApi';
import { RuleApi } from '../../api/RuleApi';
import { SourceType } from '../../api/SourceApi';
import { CurrencyCode } from '../../config/CurrencyCode';
import {
  LoanTransactionApiResponse,
  LoanTransactionPayment,
} from '../../api/Loans';
import { computedFn } from 'mobx-utils';
import {
  RuleTransactionType,
  TransactionPageType,
  TxType,
} from '../../config/Constants';
import { xeroDeepLink } from '../../utils/XeroLinks';

export class RechargeValue {
  entityId: number = 0;
  value: number = 0.0;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: RechargeValueApiResponse): RechargeValue {
    const value = new RechargeValue();
    value.entityId = apiData.organisationId;
    value.value = apiData.value;

    return value;
  }
}

export class Transaction {
  id: number = 0;
  organisationId: number = 0;
  externalId: string = '';
  journalSourceId: string = '';
  date: DateTime = DateTime.now();
  created: string = '';
  amount: number = 0;
  currency: CurrencyCode = CurrencyCode.GBP;
  nominalCode: string = '';
  trackingCategory: Map<string, string> = new Map();
  contact: string = '';
  contactLink: string = '';
  title: string = '';
  description: string = '';
  type: TxType;
  sourceType: TransactionSource;
  sourceId: string;
  source: SourceType;
  rechargeUrl?: string;
  payments?: LoanTransactionPayment[];
  amountDue?: number;

  // Match data
  matchId: number = 0;
  detectedRuleApportionmentId: number = 0;
  overrideRuleApportionmentId: number = 0;
  doNotRecharge: boolean = false;
  rechargeValues: RechargeValue[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: TransactionApiResponse): Transaction {
    const transaction = new Transaction();

    if (apiData) {
      transaction.id = apiData.id;
      transaction.externalId = apiData.externalId;
      transaction.organisationId = apiData.organisationId;
      transaction.journalSourceId = apiData.journalSourceId;
      transaction.date = DateTime.fromISO(apiData.date);
      transaction.created = apiData.created;
      transaction.amount = apiData.amount;
      transaction.currency = apiData.currency as CurrencyCode;
      transaction.nominalCode = apiData.nominalCode;
      transaction.trackingCategory = apiData.trackingCategory
        ? new Map(
            apiData.trackingCategory
              .split(',')
              .map(cat => [cat.split(':')[0], cat])
          )
        : new Map();
      transaction.contact = apiData.contact;
      transaction.contactLink = apiData.contactLink;
      transaction.title = apiData.title;
      transaction.description = apiData.description;
      transaction.type = apiData.type as TxType;
      transaction.sourceType = apiData.sourceType as TransactionSource;
      transaction.source = apiData.source as SourceType;
    }

    return transaction;
  }

  static fromMatchApi(match: TransactionMatchApiResponse): Transaction {
    const transaction = Transaction.fromApi(match.transaction);
    transaction.organisationId = match.organisationId;

    transaction.matchId = match.id;
    if (
      match.detectedRuleApportionmentId ||
      match.overrideRuleApportionmentId
    ) {
      if (match.detectedRuleApportionmentId)
        transaction.detectedRuleApportionmentId =
          match.detectedRuleApportionmentId;
      if (match.overrideRuleApportionmentId)
        transaction.overrideRuleApportionmentId =
          match.overrideRuleApportionmentId;
      if (match.rechargeValues)
        transaction.rechargeValues = match.rechargeValues.map(value =>
          RechargeValue.fromApi(value)
        );
      transaction.doNotRecharge = match.doNotRecharge;
    }

    return transaction;
  }

  static fromLoansApi(apiData: LoanTransactionApiResponse): Transaction {
    const transaction = new Transaction();

    transaction.id = apiData.transactionId;
    transaction.amount = apiData.amount;
    transaction.currency = apiData.currency as CurrencyCode;
    transaction.date = DateTime.fromISO(apiData.date);
    transaction.description = apiData.description;
    transaction.journalSourceId = apiData.journalSourceId;
    transaction.sourceType = apiData.sourceType as TransactionSource;
    transaction.title = apiData.title;
    transaction.rechargeUrl = apiData.rechargeUrl;
    transaction.payments = apiData?.payments?.map(p =>
      Object.assign(new LoanTransactionPayment(), p)
    );
    transaction.amountDue = apiData.amountDue;

    return transaction;
  }

  isUnmatched = computedFn(
    (): boolean =>
      this.doNotRecharge ||
      (this.detectedRuleApportionmentId === 0 &&
        this.overrideRuleApportionmentId === 0)
  );

  get assignedRule(): number {
    return !this.overrideRuleApportionmentId
      ? this.detectedRuleApportionmentId
      : this.overrideRuleApportionmentId;
  }

  get totalRecharge() {
    return this.rechargeValues
      .map(recharge => recharge.value)
      .reduce((a, b) => a + b, 0);
  }

  matchesPage = computedFn((page: TransactionPageType): boolean =>
    page === TransactionPageType.TRANSACTIONS
      ? this.sourceType !== TransactionSource.RECHARGE
      : this.sourceType === TransactionSource.RECHARGE
  );

  get ruleTransactionType(): RuleTransactionType {
    switch (this.type) {
      case TxType.EXPENSE:
        return RuleTransactionType.expense;
      case TxType.INCOME:
        return RuleTransactionType.income;
      case TxType.ASSET:
      case TxType.LIABILITY:
        return RuleTransactionType.balancesheet;
      default:
        return RuleTransactionType.none;
    }
  }

  setOverrideRule = (
    groupId: number,
    calculationId: number,
    ruleId: number
  ): Promise<Transaction> => {
    return RuleApi.updateMatch(
      groupId,
      calculationId,
      this.matchId,
      ruleId,
      false,
      false
    ).then(() => this);
  };

  setDoNotRecharge = (
    groupId: number,
    calculationId: number
  ): Promise<Transaction> => {
    return RuleApi.updateMatch(
      groupId,
      calculationId,
      this.matchId,
      null,
      true,
      false
    ).then(() => {
      this.doNotRecharge = true;
      return this;
    });
  };

  resetOverride = (groupId: number, calculationId: number) => {
    if (this.overrideRuleApportionmentId) {
      return CalculationApi.batchUpdate(groupId, calculationId, {
        matchIds: [this.matchId],
        doNotRecharge: false,
        ruleApportionmentId: null,
        reset: true,
      }).then(() => {
        this.doNotRecharge = false;
        this.overrideRuleApportionmentId = 0;
      });
    }
    return Promise.resolve();
  };

  apportionmentForEntity = (entityId: number): number => {
    const recharge = this.rechargeValues.find(r => r.entityId === entityId);
    return recharge ? recharge.value : 0.0;
  };

  deeplinkUrl = (shortcode: string): string => {
    return xeroDeepLink(shortcode, this.sourceType, this.journalSourceId);
  };

  matchesSearch = (value: string): boolean => {
    return (
      (this.description && this.description.toLowerCase().includes(value)) ||
      (this.title && this.title.toLowerCase().includes(value))
    );
  };

  isNew = computedFn((lockDate: DateTime) => {
    return (
      lockDate?.isValid &&
      this.sourceType !== TransactionSource.RECHARGE &&
      this.ruleTransactionType !== RuleTransactionType.none &&
      DateTime.fromISO(this.created) > lockDate
    );
  });

  accountsType = () => {
    switch (this.sourceType) {
      case TransactionSource.ACCREC:
      case TransactionSource.ACCRECCREDIT:
        return 'AccountsReceivable';
      case TransactionSource.ACCPAY:
      case TransactionSource.ACCPAYCREDIT:
        return 'AccountsPayable';
    }
  };
}
